'use client';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { forwardRef, useEffect, useRef } from 'react';
import '@/styles/phone-input.css';

interface PhoneInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  value?: string;
  onPhoneChange?: (value: string) => void;
  wrapperClass?: string;
}

const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ className, value, onPhoneChange, wrapperClass, ...props }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const onPhoneChangeRef = useRef(onPhoneChange);
    const itiRef = useRef<any>(null);

    useEffect(() => {
      onPhoneChangeRef.current = onPhoneChange;
    }, [onPhoneChange]);

    useEffect(() => {
      const currentInput = inputRef.current;
      const loadScripts = async () => {
        // ... (existing script loading logic)
        if (!(window as any).intlTelInput) {
          const script = document.createElement('script');
          script.src =
            'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.21/js/intlTelInput.min.js';
          script.async = true;
          document.body.appendChild(script);

          const utilsScript = document.createElement('script');
          utilsScript.src =
            'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.21/js/utils.js';
          document.body.appendChild(utilsScript);

          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href =
            'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.21/css/intlTelInput.css';
          document.head.appendChild(link);

          await new Promise((resolve) => {
            script.onload = resolve;
          });
        }

        setTimeout(initIti, 500);
      };

      const handleChange = () => {
        if (onPhoneChangeRef.current && itiRef.current) {
          const number = itiRef.current.getNumber();
          onPhoneChangeRef.current(number);
        }
      };

      const initIti = () => {
        if (currentInput && (window as any).intlTelInput) {
          if (itiRef.current) {
            itiRef.current.destroy();
          }

          itiRef.current = (window as any).intlTelInput(currentInput, {
            initialCountry: 'gb',
            preferredCountries: ['gb', 'us'],
            utilsScript:
              'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.21/js/utils.js',
            dropdownContainer: document.body,
            separateDialCode: true,
          });

          // Fix: intlTelInput sets aria-activedescendant pointing to a preferred country list item
          // (e.g. "iti-0__item-gb-preferred") that may not exist in the DOM when dropdown is closed.
          // Remove the invalid aria-activedescendant attribute to fix aria-valid-attr-value audit.
          const selectedFlag = currentInput.closest('.iti')?.querySelector('.iti__selected-flag');
          if (selectedFlag) {
            selectedFlag.removeAttribute('aria-activedescendant');
          }

          if (value) {
            itiRef.current.setNumber(value);
          }

          currentInput.addEventListener('countrychange', handleChange);
          currentInput.addEventListener('input', handleChange);
        }
      };

      loadScripts();

      return () => {
        if (itiRef.current) {
          itiRef.current.destroy();
          itiRef.current = null;
        }
        if (currentInput) {
          currentInput.removeEventListener('countrychange', handleChange);
          currentInput.removeEventListener('input', handleChange);
        }
      };
    }, []); // Only initialize once on mount

    useEffect(() => {
      if (itiRef.current && value) {
        const currentVal = itiRef.current.getNumber();
        if (currentVal !== value) {
          itiRef.current.setNumber(value);
        }
      }
    }, [value]);

    return (
      <div className={cn('phone-input-final-container relative w-full', wrapperClass)}>
        <input
          ref={(node) => {
            (inputRef as any).current = node;
            if (typeof ref === 'function') ref(node);
            else if (ref) (ref as any).current = node;
          }}
          type="tel"
          className={cn(
            '!pl-[72px] h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs outline-none transition-[color,box-shadow] selection:bg-primary selection:text-primary-foreground file:inline-flex file:h-7 file:border-0 file:bg-transparent file:font-medium file:text-foreground file:text-sm placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:bg-input/30',
            className
          )}
          {...props}
          defaultValue={value as string}
        />
      </div>
    );
  }
);

PhoneInput.displayName = 'PhoneInput';

export { PhoneInput };
